// Quantity-aware stock: optional amounts the user entered, drawn down in order so two meals can't
// both count on the same 500 g of chicken.
//
// Amounts are never guessed. An item without an amount, or in a unit USDA portion data can't
// convert, counts as enough, which is how the app behaved before amounts existed.

import { gramsOf } from './nutrition/estimate';
import type { Recipe, RecipeIngredient, Unit } from './recipes';
import { lineage, type IngredientId } from './taxonomy';

export interface Amount { amount: number; unit: Unit }
export interface StockItem { ingredientId: IngredientId; amount: Amount | null }

/** `qty` in unit `from` expressed in unit `to` for this ingredient, or null when they can't be bridged. */
export function convert(id: IngredientId, qty: number, from: Unit, to: Unit): number | null {
  if (from === to) return qty;
  if (from === 'pinch' || to === 'pinch') return null; // weighed as 0 g for nutrition, not a real amount
  const grams = gramsOf({ id, qty, unit: from, role: 'core' });
  const perUnit = gramsOf({ id, qty: 1, unit: to, role: 'core' });
  return grams !== null && perUnit ? grams / perUnit : null;
}

export interface Stock {
  /** Take a recipe ingredient (times `scale`); returns the shortfall in the recipe's unit, 0 when covered or untracked. */
  draw: (ing: RecipeIngredient, scale?: number) => number;
  /** What is left of each tracked item, in its own unit. */
  remaining: () => Map<IngredientId, Amount>;
}

export function createStock(items: StockItem[]): Stock {
  const left = new Map<IngredientId, Amount>();
  for (const i of items) if (i.amount) left.set(i.ingredientId, { ...i.amount });

  return {
    draw(ing, scale = 1) {
      // Measured cooked (day-old rice) isn't the same stock as the dry item in the cupboard.
      if (ing.qty === undefined || !ing.unit || ing.cooked) return 0;
      const qty = ing.qty * scale;
      const unit = ing.unit;
      // Owning a child satisfies its parent (cherry tomatoes cover "tomatoes").
      const itemId = [...left.keys()].find((k) => lineage(k).includes(ing.id));
      if (!itemId || qty <= 0) return 0;
      const have = left.get(itemId)!;
      const need = convert(itemId, qty, unit, have.unit);
      if (need === null) return 0;
      if (have.amount >= need) {
        have.amount -= need;
        return 0;
      }
      const short = qty * ((need - have.amount) / need);
      have.amount = 0;
      return short;
    },
    remaining: () => new Map([...left].map(([k, v]) => [k, { ...v }])),
  };
}

/** Kitchen amounts after cooking `recipe` for `servings`; untracked items are unchanged. */
export function afterCooking(items: StockItem[], recipe: Recipe, servings: number): Map<IngredientId, Amount> {
  const stock = createStock(items);
  for (const ing of recipe.ingredients) stock.draw(ing, servings / recipe.servings);
  return stock.remaining();
}
